import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Vote, 
  Users, 
  CheckCircle2, 
  Plus,
  ArrowRight,
  TrendingUp,
  Building2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Layout } from "../components/Layout";
import { formatDateTz } from "../lib/dateUtils";
import { getDashboardStats, getConsultes } from "../lib/api";
import { toast } from "sonner";
import { API_URL } from "../lib/config";

export default function DashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    total_consultes: 0,
    active_consultes: 0,
    total_votes: 0,
    total_census: 0
  });
  const [recentConsultes, setRecentConsultes] = useState([]);
  const [loading, setLoading] = useState(true);
  const entityName = localStorage.getItem("entity_name") || t('nav.entity');
  const entityLogo = localStorage.getItem("entity_logo");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, consultesRes] = await Promise.all([
        getDashboardStats(),
        getConsultes()
      ]);
      setStats(statsRes.data);
      setRecentConsultes(consultesRes.data.slice(0, 5));
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, consulta) => {
    const isVoting = status === "published" && consulta?.start_date && consulta?.end_date &&
      new Date() >= new Date(consulta.start_date) && new Date() <= new Date(consulta.end_date);

    if (isVoting) {
      return <span className="status-badge status-active">{t('adminConsultes.voting')}</span>;
    }

    const badges = {
      draft: { class: "status-draft", label: t('status.draft') },
      pending_publication: { class: "status-pending", label: t('status.pendingPublication') },
      published: { class: "status-pending", label: t('status.published') },
      pending_closure: { class: "status-closed", label: t('status.pendingClosure') },
      closed: { class: "status-closed", label: t('status.closed') },
      pending_blockchain: { class: "status-pending-blockchain", label: t('status.pendingBlockchain') },
      expired: { class: "status-draft", label: t('status.expired') },
      scrutinized: { class: "status-scrutinized", label: t('status.scrutinized') }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  const statCards = [
    { 
      label: t('dashboard.totalConsultes'), 
      value: stats.total_consultes, 
      icon: Vote,
      color: "bg-blue-50 text-blue-600"
    },
    { 
      label: t('dashboard.activeConsultes'), 
      value: stats.active_consultes, 
      icon: TrendingUp,
      color: "bg-emerald-50 text-emerald-600"
    },
    { 
      label: t('dashboard.totalVotes'), 
      value: stats.total_votes, 
      icon: CheckCircle2,
      color: "bg-violet-50 text-violet-600",
      subtitle: stats.last_closed_title
    },
    { 
      label: t('dashboard.censusPersons'), 
      value: stats.total_census, 
      icon: Users,
      color: "bg-amber-50 text-amber-600",
      subtitle: stats.last_closed_title
    },
  ];

  return (
    <Layout>
      <div className="content-container" data-testid="dashboard-page">
        {/* Header with Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-6">
            {entityLogo && (
              <div className="flex-shrink-0">
                <img 
                  src={`${API_URL}${entityLogo}`}
                  alt={entityName}
                  className="w-20 h-20 object-contain rounded-xl border border-slate-200 bg-white shadow-sm"
                />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-heading font-bold text-slate-900">
                {t('dashboard.welcome')}, {entityName}
              </h1>
              <p className="text-slate-500 mt-1">
                {t('dashboard.manageSubtitle')}
              </p>
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            >
              {statCards.map((stat, index) => (
                <div
                  key={stat.label}
                  className="stat-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="stat-value">{stat.value}</p>
                      <p className="stat-label">{stat.label}</p>
                      {stat.subtitle && (
                        <p className="text-xs text-slate-400 mt-1 truncate max-w-[180px]" title={stat.subtitle}>
                          {stat.subtitle}
                        </p>
                      )}
                    </div>
                    <div className={`p-3 rounded-lg ${stat.color}`}>
                      <stat.icon className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Quick Actions & Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border border-slate-200 rounded-lg p-6"
              >
                <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
                  {t('dashboard.quickActions')}
                </h2>
                <div className="space-y-3">
                  <Link to="/consultes/new">
                    <Button 
                      className="w-full justify-between bg-slate-900 hover:bg-slate-800"
                      data-testid="new-consulta-btn"
                    >
                      <span className="flex items-center">
                        <Plus className="h-4 w-4 mr-2" />
                        {t('dashboard.newConsulta')}
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/consultes">
                    <Button 
                      variant="outline" 
                      className="w-full justify-between"
                      data-testid="view-consultes-btn"
                    >
                      <span className="flex items-center">
                        <Vote className="h-4 w-4 mr-2" />
                        {t('dashboard.viewConsultes')}
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>

              {/* Recent Consultes */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-semibold text-slate-900">
                    {t('dashboard.recentConsultes')}
                  </h2>
                  <Link 
                    to="/consultes"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>

                {recentConsultes.length === 0 ? (
                  <div className="empty-state py-8">
                    <Vote className="empty-state-icon h-12 w-12" />
                    <p className="text-slate-500">{t('dashboard.noConsultesYet')}</p>
                    <Link to="/consultes/new">
                      <Button variant="outline" className="mt-4" data-testid="create-first-consulta">
                        <Plus className="h-4 w-4 mr-2" />
                        {t('dashboard.createFirstConsulta')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentConsultes.map((consulta) => (
                      <Link
                        key={consulta.id}
                        to={`/consultes/${consulta.id}`}
                        className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors"
                        data-testid={`consulta-item-${consulta.id}`}
                      >
                        <div>
                          <h3 className="font-medium text-slate-900">{consulta.title}</h3>
                          <p className="text-sm text-slate-500">
                            {formatDateTz(consulta.created_at, consulta.entity_timezone)}
                          </p>
                        </div>
                        {getStatusBadge(consulta.status, consulta)}
                      </Link>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
